import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, Play, RefreshCw, AlertTriangle, AlertCircle, CheckCircle,
  Info, ExternalLink, Plus, ChevronDown, ChevronUp, Loader
} from 'lucide-react';
import { reputationApi, dealsApi } from '../services/api';
import './ReputationRisk.css';

const RISK_CONFIG = {
  low:      { color: '#10b981', bg: '#d1fae5', label: 'Low Risk' },
  medium:   { color: '#f59e0b', bg: '#fef3c7', label: 'Medium Risk' },
  high:     { color: '#ef4444', bg: '#fee2e2', label: 'High Risk' },
  critical: { color: '#7c3aed', bg: '#ede9fe', label: 'Critical Risk' },
  unknown:  { color: '#6b7280', bg: '#f3f4f6', label: 'Unknown' },
};

const SENTIMENT_CONFIG = {
  positive: { color: '#10b981', label: 'Positive' },
  negative: { color: '#ef4444', label: 'Negative' },
  neutral:  { color: '#6b7280', label: 'Neutral' },
  mixed:    { color: '#f59e0b', label: 'Mixed' },
};

const CATEGORY_ICONS = {
  'Employee Reviews':     '👥',
  'Customer Reviews':     '⭐',
  'News & Media':         '📰',
  'Legal & Regulatory':   '⚖️',
  'Leadership':           '👤',
};

function RiskBadge({ level }) {
  const cfg = RISK_CONFIG[level] || RISK_CONFIG.unknown;
  return (
    <span style={{
      backgroundColor: cfg.bg,
      color: cfg.color,
      padding: '2px 10px',
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 600,
    }}>
      {cfg.label}
    </span>
  );
}

function FindingCard({ finding, onAddToNotes, canEdit }) {
  const [expanded, setExpanded] = useState(false);
  const [addState, setAddState] = useState('idle'); // 'idle' | 'loading' | 'done'
  const sentCfg = SENTIMENT_CONFIG[finding.sentiment] || SENTIMENT_CONFIG.neutral;

  const handleAdd = async () => {
    setAddState('loading');
    await onAddToNotes(finding);
    setAddState('done');
    setTimeout(() => setAddState('idle'), 3000);
  };

  return (
    <div className="rep-finding-card">
      <div className="rep-finding-header" onClick={() => setExpanded(e => !e)}>
        <div className="rep-finding-left">
          <span className="rep-category-icon">{CATEGORY_ICONS[finding.category] || '🔍'}</span>
          <div>
            <div className="rep-finding-title">{finding.title}</div>
            <div className="rep-finding-meta">
              <span style={{ color: sentCfg.color, fontSize: 12, fontWeight: 500 }}>
                {sentCfg.label}
              </span>
              {finding.source && (
                <span className="rep-source-tag">{finding.source}</span>
              )}
            </div>
          </div>
        </div>
        <div className="rep-finding-right">
          <RiskBadge level={finding.risk_level} />
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {expanded && (
        <div className="rep-finding-body">
          <p className="rep-finding-detail">{finding.detail}</p>
          <div className="rep-finding-actions">
            {finding.url && (
              <a
                href={finding.url}
                target="_blank"
                rel="noopener noreferrer"
                className="rep-link-btn"
              >
                <ExternalLink size={13} /> View Source
              </a>
            )}
            {canEdit && (
              addState === 'done' ? (
                <span className="rep-added-msg">
                  <CheckCircle size={13} /> Added to findings
                </span>
              ) : (
                <button className="rep-add-finding-btn" onClick={handleAdd} disabled={addState === 'loading'}>
                  {addState === 'loading'
                    ? <><Loader size={13} className="rep-spin" /> Adding…</>
                    : <><Plus size={13} /> Add to Findings</>
                  }
                </button>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ReputationRisk({ deal, canEdit = true }) {
  const [analysis, setAnalysis]           = useState(null);
  const [historicAnalysis, setHistoricAnalysis] = useState(null); // loaded but not shown by default
  const [showHistory, setShowHistory]     = useState(false);
  const [loading, setLoading]             = useState(true);
  const [running, setRunning]             = useState(false);
  const [polling, setPolling]             = useState(false);
  const [error, setError]                 = useState(null);
  const [pendingId, setPendingId]         = useState(null);
  const [addedFindings, setAddedFindings] = useState(new Set());

  // Form state
  const [companyName, setCompanyName] = useState(deal?.name || '');
  const [website, setWebsite]         = useState('');
  const [execInput, setExecInput]     = useState('');
  const [executives, setExecutives]   = useState([]);

  // Load most recent analysis on mount (small delay to let backend insert pending record)
  useEffect(() => {
    if (!deal?.id) return;
    const t = setTimeout(fetchLatest, 800);
    return () => clearTimeout(t);
  }, [deal?.id]); // eslint-disable-line

  const fetchLatest = async () => {
    setLoading(true);
    try {
      const res = await reputationApi.getLatest(deal.id);
      const data = res.data;
      if (data) {
        if (data.status === 'pending') {
          const ageMs = Date.now() - new Date(data.created_at).getTime();
          if (ageMs > 5 * 60 * 1000) return; // stale — show form
          // Recent pending from a manual trigger — resume polling
          setAnalysis(data);
          startPolling(data.id);
        } else if (data.status === 'completed') {
          // Store as history but don't show it by default
          setHistoricAnalysis(data);
        }
      }
    } catch (e) {
      if (e.response?.status !== 404) {
        console.error('[ReputationRisk] fetch latest', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const startPolling = useCallback((id) => {
    setPendingId(id);
    setPolling(true);
  }, []);

  // Poll for result when pending
  useEffect(() => {
    if (!polling || !pendingId) return;
    const interval = setInterval(async () => {
      try {
        const res = await reputationApi.getResult(deal.id, pendingId);
        const data = res.data;
        if (data.status === 'completed' || data.status === 'failed') {
          setAnalysis(data);
          if (data.status === 'completed') setHistoricAnalysis(data);
          setShowHistory(false);
          setPolling(false);
          setPendingId(null);
          setRunning(false);
        }
      } catch (e) {
        console.error('[ReputationRisk] poll', e);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [polling, pendingId, deal?.id]); // eslint-disable-line

  const runAnalysis = async () => {
    if (!companyName.trim()) return;
    setRunning(true);
    setError(null);
    try {
      const res = await reputationApi.analyze(deal.id, {
        companyName: companyName.trim(),
        website: website.trim(),
        executives,
      });
      const data = res.data;
      setAnalysis({ ...data, findings: [], summary: '' });
      startPolling(data.id);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setRunning(false);
    }
  };

  const addExecutive = () => {
    const name = execInput.trim();
    if (name && !executives.includes(name)) {
      setExecutives(prev => [...prev, name]);
    }
    setExecInput('');
  };

  const removeExecutive = (name) => {
    setExecutives(prev => prev.filter(e => e !== name));
  };

  const handleAddToNotes = async (finding) => {
    if (!deal?.id) return;
    const key = `${finding.title}-${finding.category}`;
    try {
      await dealsApi.addNote(deal.id, {
        category: 'Reputational Risk',
        question: finding.title,
        finding: `[${finding.risk_level?.toUpperCase()} | ${finding.sentiment}] ${finding.detail}${finding.url ? `\n\nSource: ${finding.url}` : ''}`,
        date: new Date().toISOString().split('T')[0],
      });
      setAddedFindings(prev => new Set([...prev, key]));
    } catch (e) {
      console.error('[ReputationRisk] addToNotes', e);
    }
  };

  const isRunning = running || polling || analysis?.status === 'pending';
  const displayedAnalysis = showHistory ? historicAnalysis : (analysis?.status === 'completed' ? analysis : null);
  const overallRisk = displayedAnalysis?.overall_risk || 'unknown';
  const riskCfg = RISK_CONFIG[overallRisk] || RISK_CONFIG.unknown;
  const grouped = (displayedAnalysis?.findings || []).reduce((acc, f) => {
    const cat = f.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(f);
    return acc;
  }, {});

  if (!deal?.id) return (
    <div style={{ padding: 32, color: '#6b7280', fontSize: 14 }}>Select a deal to run reputational analysis.</div>
  );

  return (
    <div className="rep-container">
      {/* Header */}
      <div className="rep-header">
        <div className="rep-header-left">
          <Shield size={20} className="rep-header-icon" />
          <div>
            <h3 className="rep-title">Reputational Risk Analysis</h3>
            <p className="rep-subtitle">AI-powered web research on company and leadership reputation</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {historicAnalysis && !isRunning && (
            <button
              className="rep-rerun-btn"
              onClick={() => setShowHistory(h => !h)}
              style={{ margin: 0 }}
            >
              <RefreshCw size={14} />
              {showHistory ? 'Hide Previous Analysis' : 'View Previous Analysis'}
            </button>
          )}
          {displayedAnalysis?.overall_risk && (
            <div className="rep-overall-badge" style={{ backgroundColor: riskCfg.bg, color: riskCfg.color }}>
              {overallRisk === 'critical' && <AlertCircle size={16} />}
              {overallRisk === 'high'     && <AlertTriangle size={16} />}
              {overallRisk === 'medium'   && <Info size={16} />}
              {overallRisk === 'low'      && <CheckCircle size={16} />}
              <span>{riskCfg.label}</span>
            </div>
          )}
        </div>
      </div>

      {/* Config form — always shown unless actively running or viewing history */}
      {!isRunning && !showHistory && (
        <div className="rep-form-card">
          <div className="rep-form-grid">
            <div className="rep-form-group">
              <label className="rep-label">Company Name *</label>
              <input
                className="rep-input"
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                placeholder="e.g. Acme Corp"
              />
            </div>
            <div className="rep-form-group">
              <label className="rep-label">Website (optional)</label>
              <input
                className="rep-input"
                value={website}
                onChange={e => setWebsite(e.target.value)}
                placeholder="https://example.com"
              />
            </div>
          </div>

          <div className="rep-form-group">
            <label className="rep-label">Executives to Research</label>
            <div className="rep-exec-input-row">
              <input
                className="rep-input"
                value={execInput}
                onChange={e => setExecInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addExecutive()}
                placeholder="Full name — press Enter to add"
              />
              <button className="rep-add-exec-btn" onClick={addExecutive}>Add</button>
            </div>
            {executives.length > 0 && (
              <div className="rep-exec-tags">
                {executives.map(name => (
                  <span key={name} className="rep-exec-tag">
                    {name}
                    <button onClick={() => removeExecutive(name)}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {error && <p className="rep-error"><AlertCircle size={14} /> {error}</p>}

          <button
            className="rep-run-btn"
            onClick={runAnalysis}
            disabled={isRunning || !companyName.trim()}
          >
            {isRunning ? <Loader size={15} className="rep-spin" /> : <Play size={15} />}
            {isRunning ? 'Running Analysis…' : 'Run Reputational Analysis'}
          </button>
        </div>
      )}

      {/* Loading / pending state */}
      {loading && !analysis && (
        <div className="rep-status-card">
          <Loader size={20} className="rep-spin" />
          <span>Loading…</span>
        </div>
      )}

      {isRunning && analysis && (
        <div className="rep-status-card rep-status-running">
          <Loader size={20} className="rep-spin" style={{ color: '#3b82f6' }} />
          <div>
            <div style={{ fontWeight: 600 }}>Analysis in progress</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
              Claude is searching the web across employee reviews, news, legal records, and more. This takes 1–2 minutes.
            </div>
          </div>
        </div>
      )}

      {/* Failed */}
      {analysis?.status === 'failed' && !showHistory && (
        <div className="rep-status-card rep-status-failed">
          <AlertCircle size={18} style={{ color: '#ef4444' }} />
          <span>Analysis failed. Please try again.</span>
        </div>
      )}

      {/* Results — shown after a fresh run completes, or when viewing history */}
      {displayedAnalysis?.status === 'completed' && (
        <>
          {showHistory && (
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
              Ran {new Date(displayedAnalysis.created_at).toLocaleString()}
            </div>
          )}

          {/* Summary */}
          {displayedAnalysis.summary && (
            <div className="rep-summary-card">
              <h4 className="rep-section-title">Executive Summary</h4>
              <p className="rep-summary-text">{displayedAnalysis.summary}</p>
            </div>
          )}

          {/* Findings by category */}
          {Object.keys(grouped).map(category => (
            <div key={category} className="rep-category-section">
              <div className="rep-category-header">
                <span className="rep-category-emoji">{CATEGORY_ICONS[category] || '🔍'}</span>
                <h4 className="rep-category-title">{category}</h4>
                <span className="rep-category-count">{grouped[category].length}</span>
              </div>
              <div className="rep-findings-list">
                {grouped[category].map((finding, i) => {
                  const key = `${finding.title}-${finding.category}`;
                  return (
                    <FindingCard
                      key={i}
                      finding={finding}
                      onAddToNotes={() => handleAddToNotes(finding)}
                      canEdit={canEdit && !addedFindings.has(key)}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
